import constants from '@Common/Core/Constant';
import Maybe from '@Common/Core/Maybe';
import { ISelfSubscribe, MulticastDelegateBag } from '@Common/Core/MulticastDelegate';
import { IControl } from '../Controls';
import { ColumnCollection } from './ColumnCollection';
import { dataSource } from './DataSources';
import { FilterManager } from './FilterManager';
import { GridCell } from './GridCell';
import { GridColumn } from './GridColumn';
import { SimpleGridLayout } from './SimpleGridLayout';
import { GridNavigationManager } from './GridNavigationManager';
import { GridRender } from './GridRender';
import { GridResizer } from './GridResizer';
import { GridRow } from './GridRow';
import { IGridPlugin } from './Plugins/IGridPlugin';
import { RowCollection } from './RowCollection';
import {
    classNames,
    ColumnsFitType,
    defaultGridPagerSettting,
    defaultGridSetting,
    IGridPager,
    IGridPluginInitializer,
    IGridProperties,
    SelectionMode
} from './Settings';
import { SortManager } from './SortManager';
import { enumerable } from '@Common/Core/CoreDecorator';
import { IDestroyable } from '@Common/Core/Destroyable';

export class Grid<TData = any, TGridRow extends GridRow<TData> = GridRow<TData>> implements IControl {
    public readonly columnsCollection: ColumnCollection<TData>;
    public readonly gridLayout: SimpleGridLayout;
    public readonly rowsCollection: RowCollection<TData, TGridRow>;
    private readonly gridProperties: IGridProperties;
    private readonly filterManager: FilterManager;
    private readonly sortManager: SortManager;
    private readonly gridResizer: GridResizer;
    private readonly gridRender: GridRender;
    private readonly navigation: GridNavigationManager;
    private readonly eventsBag = new MulticastDelegateBag();

    public readonly onRowDblClick = this.eventsBag.create<ICallback<GridRow>>();
    public readonly onBeginEdit = this.eventsBag.create<ICallback<GridCell>>();
    public readonly onBeforeEdit = this.eventsBag.create<ICallback<GridCell>>();
    public readonly onEndEdit = this.eventsBag.create<ICallback<GridCell>>();
    public readonly onChange = this.eventsBag.create<IHandler>();
    public readonly onValidate = this.eventsBag.create<ICallback<boolean>>();

    private onDocumentMouseDown: (e: JQuery.Event) => any;
    private currentEditCell: GridCell | null = null;
    private plugins: IGridPlugin[] = [];

    constructor(prop: IGridProperties<TData>) {
        const storeData = prop.data || [];
        delete prop.data;
        this.gridProperties = $.extend(true, {}, defaultGridSetting, prop);
        const pagerSetting: IGridPager = $.extend({}, defaultGridPagerSettting, this.gridProperties.pager);
        this.gridRender = new GridRender(this.gridProperties);
        this.gridRender.render();
        this.sortManager = new SortManager(this, this.gridRender);
        this.gridResizer = new GridResizer(this, this.gridRender);
        this.filterManager = new FilterManager(this, this.gridRender);
        this.gridRender.root.attr('fit', ColumnsFitType[this.gridProperties.columnsFit]);

        this.gridRender.bodyTable.singleClick((c, eventData: JQuery.TriggeredEvent) => {
            const cell = this.getCell(eventData);
            const row = cell.getRow();
            this.processSelection(row);
            this.processEdit(cell);
        }, 'td.simpleGrid-cell');

        this.gridRender.bodyTable.doubleClick((eventData: JQuery.TriggeredEvent) => {
            const gridCell = this.getCell(eventData);
            if (gridCell && !gridCell.canEdit()) {
                eventData.stopPropagation();
                this.onRowDblClick(gridCell.getRow());
            }
        }, 'td.simpleGrid-cell');

        this.gridRender.bodyTable.on('keydown', 'td.simpleGrid-cell', (eventData: JQuery.Event) => {
            if (this.currentEditCell != null) {
                switch (eventData.which) {
                    case constants.keyCodes.ENTER:
                    case constants.keyCodes.ESCAPE:
                        if (eventData.which == constants.keyCodes.ESCAPE) {
                            this.currentEditCell.refresh();
                        }
                        this.endEdit(true);
                        eventData.stopPropagation();
                        this.gridRender.root.parents('[tabindex]').trigger('focus');
                        break;
                }
            }
        });

        this.rowsCollection = new RowCollection(
            this,
            this.gridProperties.rowBatchSize,
            pagerSetting.startPage,
            pagerSetting.pageSize,
            this.filterManager,
            this.sortManager,
            this.gridRender,
            Maybe(this.gridProperties)
                .read(p => p.behavior)
                .read(b => b.selectionMode)
                .valueOrDefault(SelectionMode.select)
        );
        this.rowsCollection.onChanged(() => this.onRowsChanged());

        this.navigation = new GridNavigationManager(this.gridRender, this);

        if (_.isBoolean(this.gridProperties.keyboardNav)) {
            this.navigation.enabled(this.gridProperties.keyboardNav);
        } else {
            if (this.gridProperties.keyboardNav && 'newRowFactory' in this.gridProperties.keyboardNav) {
                this.navigation.enabled(true);
                this.navigation.newRowFactory = this.gridProperties.keyboardNav.newRowFactory;
            }
        }

        this.gridLayout = new SimpleGridLayout(this, this.gridRender, this.gridProperties, pagerSetting);
        this.gridLayout.scrolledToEnd = () => {
            this.scrollToEnd();
        };
        this.gridLayout.pageChange = (page: number) => {
            this.rowsCollection.setCurrentPage(page);
        };
        this.columnsCollection = new ColumnCollection(this, this.gridRender, this.rowsCollection);

        this.sortManager.init();
        this.gridResizer.init();
        this.filterManager.init(this.gridProperties.columns);

        this.value(storeData);

        this.sortManager.resort();
        this.rowsCollection.attachRows();

        $(document).on(
            'mousedown',
            (this.onDocumentMouseDown = (e: JQuery.TriggeredEvent) => {
                if (!this.mouseInEditCell(e)) {
                    this.endEdit(false);
                }
            })
        );

        this.initPlugins(prop.plugins);
    }

    @enumerable
    public get onAfterAddRow(): ISelfSubscribe<ICallback<GridRow>> {
        return this.rowsCollection.onAfterAddRow;
    }

    public destroy(): void {
        try {
            $(document).off('mousedown', this.onDocumentMouseDown);
            this.onDocumentMouseDown = _.noop;
            this.eventsBag.destroy();
            this.filterManager.destroy();
            this.gridLayout.destroy();
            this.gridRender.destroy();
            this.columnsCollection.destroy();
        } catch (e) {
            console.warn(e);
        }
    }

    public onRowSelectedChanged(fn: (newSelectedRow: TGridRow) => void): void {
        this.rowsCollection.onRowSelectedChanged(fn);
    }

    public onSorting(fn: () => void): void {
        this.sortManager.onSorting(fn);
    }

    public onFiltering(fn: () => void): void {
        this.filterManager.onFiltering(fn);
    }

    public onCellChanged(fn: (cell: GridCell) => void): void {
        this.rowsCollection.onCellChanged(fn);
    }

    public onAfterDeleteRow(fn: (data?: any[]) => void): void {
        this.rowsCollection.onAfterDeleteRow(fn);
    }

    public endEdit(force: boolean): void {
        const currentEditCell = this.currentEditCell;
        if (currentEditCell != null) {
            if (currentEditCell.getRow().isRendered) {
                if (currentEditCell.endEdit(force)) {
                    this.setCurrentEditCell(null);
                    this.gridLayout.doLayout();
                    this.onEndEdit(currentEditCell);
                }
            } else {
                this.setCurrentEditCell(null);
            }
        }
    }

    public getCurrentEditCell(): GridCell | null {
        return this.currentEditCell;
    }

    public getRealHeight(): number {
        return this.gridRender.headerContainer.outerHeight() + this.gridRender.bodyTable.outerHeight();
    }

    public getControl(): JQuery {
        return this.gridRender.root;
    }

    public visible(value?: boolean): boolean {
        return true;
    }

    public setState(enabled: boolean): void {
        this.enabled(enabled);
    }

    public enabled(value?: boolean): boolean {
        if (value === undefined) {
            return !this.gridRender.root.hasClass(classNames.disable);
        }
        this.gridRender.root.toggleClass(classNames.disable, !value);
        return value;
    }

    public readonly(value?: boolean): boolean {
        if (value === undefined) {
            return !this.gridRender.root.hasClass(classNames.readonly);
        }
        if (value) {
            this.gridRender.root.addClass(classNames.readonly);
        } else {
            this.gridRender.root.removeClass(classNames.readonly);
        }
        return value;
    }

    public value(data?: any | dataSource.IDataSource | any[]): any {
        return this.rowsCollection.dataSource(data);
    }

    public appendData(data: any | any[]): TGridRow[] {
        if (data instanceof Array) {
            return this.rowsCollection.append(data);
        } else {
            return this.appendData([data]);
        }
    }

    public columns(): GridColumn[] {
        return this.columnsCollection.ToArray();
    }

    public findColumn(column: keyof TData | number): any {
        try {
            if (typeof column === 'string') {
                return this.columnsCollection.getByDataIndex(column);
            }
            if (typeof column === 'number') {
                return this.columnsCollection.get(column);
            }
        } catch (e) {
            //
        }
        return null;
    }

    public getColumnByDataIndex(dataIndex: keyof TData): GridColumn {
        const findColumn = _.find(this.columns(), col => col.dataIndex() === dataIndex);
        if (!findColumn) {
            throw new Error(`dataIndex '${dataIndex}' is not found`);
        }
        return findColumn;
    }

    public visibilityRows(): TGridRow[] {
        return this.rowsCollection.getVisibilityRows();
    }

    public rows(): TGridRow[] {
        return this.rowsCollection.ToArray();
    }

    public selectedRow(): TGridRow {
        return this.rowsCollection.getSelectedRow();
    }

    public adjust(width?: number, height?: number): void {
        if (width !== undefined) {
            const heightValue = height || this.gridProperties.height || 'auto';

            this.gridRender.root.css({ width, height: heightValue });
            this.gridLayout.reset();
        }

        this.gridLayout.doLayout();
        this.gridResizer.render();
    }

    public clearAllRows(): void {
        this.deleteRows(() => true);
    }

    public deleteRows(arg: number | number[] | ((row: GridRow) => boolean)): void {
        this.rowsCollection.deleteRows(arg);
    }

    public refresh(): void {
        this.rowsCollection.attachRows(true);
        this.gridLayout.doLayout();
    }

    public beginLoad(): void {
        this.rowsCollection.beginLoad();
    }

    public endLoad(): void {
        this.rowsCollection.endLoad();
    }

    public addPlugin(plugin: IGridPlugin): void {
        this.plugins.push(plugin);
    }

    public validate(): void {
        const validator = this.gridProperties.validator;
        if (validator) {
            this.onValidate(validator(this));
        } else {
            if (this.onValidate.canFire) {
                const valid = this.rows().some(row => row.cells().some(cell => !cell.isValid()));
                this.onValidate(valid);
            }
        }
    }

    public editNextCell(leftToRight = true): boolean {
        if (this.currentEditCell != null) {
            const newCell = this.findNextEditableCell(this.currentEditCell, leftToRight);
            if (newCell != null) {
                this.editCell(newCell);
                return true;
            }
        }
        return false;
    }

    public editCell(cell: GridCell): void {
        if (this.currentEditCell) {
            this.currentEditCell.endEdit(true);
        }
        cell.getRow().isSelected(true);
        cell.beginEdit();
        this.setCurrentEditCell(cell);
    }

    public focus(): void {
        this.gridRender.bodyTable.trigger('focus');
    }

    private processSelection(row: GridRow): void {
        const isRowSelect = this.rowsCollection.selectionMode === SelectionMode.select ? true : !row.isSelected();
        row.isSelected(isRowSelect);
    }

    private processEdit(gridCell: GridCell): void {
        if (gridCell == this.currentEditCell) {
            return;
        }
        this.endEdit(true);
        if (gridCell && gridCell.canEdit()) {
            this.setCurrentEditCell(gridCell);
            this.onBeforeEdit(this.currentEditCell);
            this.currentEditCell.beginEdit();
            this.onBeginEdit(this.currentEditCell);
            this.gridLayout.doLayout();
        }
    }

    private mouseInEditCell(e: JQuery.TriggeredEvent): boolean {
        return this.currentEditCell != null && this.mouseIn(e, this.currentEditCell.tableCell());
    }

    private mouseInGrid(e: JQuery.TriggeredEvent): boolean {
        return this.mouseIn(e, this.gridRender.bodyTable) || this.mouseIn(e, this.gridRender.headerTable);
    }

    private mouseIn(e: JQuery.TriggeredEvent, elem: JQuery): boolean {
        return $(e.target)
            .parents()
            .toArray()
            .some(parent => parent == elem[0]);
    }

    private setCurrentEditCell(cell: GridCell | null): void {
        this.currentEditCell = cell;
    }

    private onRowsChanged(): void {
        this.onChange();
        this.firePluginChange();
        this.validate();
        this.navigation.rowsChanged();
    }

    private getCell(event: JQuery.TriggeredEvent): GridCell {
        const tableCell = $(event.currentTarget);
        const index = tableCell.parent().children().index(tableCell);
        const gridRow: TGridRow = tableCell.parent().data('rowInstance') as any;
        return gridRow.getCell(index);
    }

    private scrollToEnd(): void {
        this.rowsCollection.attachNextBatch();
    }

    private firePluginChange(): void {
        this.plugins.forEach((plug: IGridPlugin) => {
            plug.gridChange();
        });
    }

    private findNextEditableCell(cell: GridCell, leftToRight: boolean): GridCell {
        const startRow = this.currentEditCell.getRow() as TGridRow;
        let newEditCell = startRow.findNextEditableCell(this.currentEditCell.getColumn(), leftToRight);
        if (newEditCell != null) {
            return newEditCell;
        }
        let newEditRow = this.rowsCollection.findNextVisibilityRow(startRow, leftToRight);
        do {
            newEditCell = newEditRow.findNextEditableCell(null, leftToRight);
            if (newEditCell != null) {
                return newEditCell;
            }
            newEditRow = this.rowsCollection.findNextVisibilityRow(newEditRow, leftToRight);
        } while (newEditRow !== startRow);
        return null;
    }

    private initPlugins(plugins: IGridPluginInitializer[]): void {
        this.plugins = _.map(plugins, p => p(this));
    }
}

export interface ICellEditor {
    refresh(): void;

    beginEdit(preview?: boolean): void;

    endEdit(preview?: boolean): void;

    isInActiveMode(): boolean;

    canTab?(): boolean;

    focus?(): void;

    destroy(): void;
}

export interface ICellEditorWithText {
    getText(): string;
}

export interface IGridColumnEditor extends Partial<IDestroyable> {
    readonly init?: (column: GridColumn) => void;

    getEditor(row: GridRow, column: GridColumn, param?: any): ICellEditor;

    buildEditorForAll(column: GridColumn): void;

    refreshAll(rows: RowCollection): void;
}
